import * as React from "react";
import { StatsigProvider } from "statsig-react";

import { AdminMain } from "./admin";
import { AppLayout, AppLayoutType } from "kw/components/AppLayout/AppLayout";
import { AppProvider } from "kanopi-components/AppProvider";
import { AuthRequired } from "kw/auth/components/AuthRequired";
import { ClaimsMain } from "kw/claims";
import { AccountMain } from "kw/account";
import { closeXDM } from "kanopi-components/utils/xdm";
import { ContactUsCard } from "./components/ContactUs/ContactUsCard";
import { GoogleMapScript } from "kanopi-components/utils/GoogleMapScript";
import { LoginPage } from "kw/auth/pages";
import { Main as BaseMain } from "kanopi-components/Main";
import { PoliciesMain } from "kw/policies";
import { QuotingMain } from "kw/quoting";
import { ReachLink } from "kanopi-components/ReachLink";
import { Router } from "@reach/router";
import { RouterOutlet } from "kw/components/RouterOutlet/RouterOutlet";
import { ScrollToTop } from "kw/util/ScrollToTop";
import { SegmentScript } from "kanopi-components/utils/SegmentScript";
import { StripeRoot } from "kanopi-components/Main/StripeRoot";
import { ThemeContext, useThemeContext } from "./themeContext";
import { ToastContainer } from "kanopi-components/ToastContainer";
import { useTheme } from "kanopi-components/theme/useTheme";
import "./global.css";
import "kw/util/validation";

import {
  PasswordResetRequestPage,
  PasswordResetRequestSuccessPage,
  PasswordResetConfirmPage,
  PasswordResetConfirmSuccessPage,
  UserHijackingHijackPage,
  UserHijackingReleasePage,
  SsoAuthenticatorLogin,
  SsoSamlAuthenticatorSuccessRedirect,
} from "kw/auth/pages";
import { Error404Page } from "kw/quoting/pages/Error404Page";
import { config } from "./settings";
import { useUser } from "./auth/useUser";
import { useGlobal } from "kanopi-components/useGlobal";

export const appConfig = {
  ...AppProvider.defaultValue,
  Link: {
    component: ReachLink,
    unstyled: ReachLink.Unstyled,
  },
  Image: {
    context: [
      require.context("./static/images", true),
      ...AppProvider.defaultValue.Image.context,
    ],
    lookupTable: {
      ...AppProvider.defaultValue.Image.lookupTable,
      "icons/covers/building-and-contents": "covers/building-and-contents.svg",
      "icons/covers/business-interruption": "covers/business-interruption.svg",
      "icons/covers/contents": "covers/contents.svg",
      "icons/covers/employee-dishonesty": "covers/employee-dishonesty.svg",
      "icons/covers/general-liability": "covers/general-liability.svg",
      "icons/covers/general-property": "covers/general-property.svg",
      "icons/covers/glass": "covers/glass.svg",
      "icons/covers/home-building": "covers/home-building.svg",
      "icons/covers/landlord": "covers/landlord.svg",
      "icons/covers/loss-of-rent": "covers/loss-of-rent.svg",
      "icons/covers/machinery": "covers/machinery.svg",
    },
  },
  viewPolicies: closeXDM,
};

function Main() {
  const user = useUser();
  const [quoting] = useGlobal("quoting");
  const [product] = useGlobal("product");
  const { theme: platformDefaultTheme, baseTheme, getThemeWithOverrides } = useTheme();
  const [themeContext, setThemeContext] = React.useState({
    platformDefaultTheme,
    basewebDefaultTheme: baseTheme,
    theme: undefined,
    getThemeWithOverrides,
  });

  React.useEffect(() => {
    document.documentElement.lang = "en";
  }, []);

  const getSubdomain = (url) => {
    const hostname = new URL(url).hostname;
    // Split hostname into parts
    const parts = hostname.split(".");
    // Check if there are more than two parts (to exclude cases like 'example.com' without a subdomain)
    if (parts.length > 2) {
      return parts.slice(0, -2).join("."); // Return all parts except the last two ('example.com')
    } else {
      return ""; // No subdomain present
    }
  };

  return platformDefaultTheme ? (
    <StatsigProvider
      sdkKey={config.STATSIG_SDK_KEY ?? ""}
      user={{
        userID: user?.uuid,
        email: user?.email,
        custom: {
          key: getSubdomain(window.location.href) ?? "local",
          product: quoting?.riskProfile?.availableProducts?.[0]?.key || "unknown",
        },
      }}
    >
      <SegmentScript />
      <GoogleMapScript />

      <ThemeContext.Provider
        value={{
          context: themeContext,
          setContext: setThemeContext,
        }}
      >
        <StripeRoot product={product}>
          <MainOutlet />
        </StripeRoot>
      </ThemeContext.Provider>
    </StatsigProvider>
  ) : (
    <></>
  );
}

const RoutesHOC = ({
                     children,
                     location,
                     layoutType,
                     ...props
                   }: {
  children: React.ReactNode;
  location?: Location;
  lazy?: boolean;
  login?: boolean;
  admin?: boolean;
  hijacking?: boolean;
  anonymous?: boolean;
  path?: string;
  layoutType?: AppLayoutType;
}) => {
  const { context } = useThemeContext();
  return (
    <AuthRequired {...props}>
      <BaseMain theme={context.theme} app={appConfig}>
        <ToastContainer />
        <AppLayout layoutType={layoutType}>
          <ScrollToTop location={location}>{children}</ScrollToTop>
        </AppLayout>
      </BaseMain>
    </AuthRequired>
  );
};

const MainOutlet = () => {
  return (
    <Router>
      <Error404Page default />
      <RoutesHOC lazy={true} path="/*">
        <RouterOutlet component={QuotingMain} path="/" isParent={true} />
      </RoutesHOC>
      <RoutesHOC admin={true} path="/admin/*" layoutType={AppLayoutType.Portal}>
        <RouterOutlet component={AdminMain} path="/" isParent={true} />
      </RoutesHOC>
      <RoutesHOC login={true} path="/policies/*" layoutType={AppLayoutType.Portal}>
        <RouterOutlet component={PoliciesMain} path="/" isParent={true} />
      </RoutesHOC>
      <RoutesHOC login={true} path="/account/*" layoutType={AppLayoutType.Portal}>
        <RouterOutlet component={AccountMain} path="/" isParent={true} />
      </RoutesHOC>
      <RoutesHOC login={true} path="/claims/*">
        <RouterOutlet component={ClaimsMain} path="/" isParent={true} />
      </RoutesHOC>
      <RoutesHOC anonymous={true} path="/login">
        <RouterOutlet
          component={LoginPage}
          path="/"
          nextUrl="/policies/"
          layoutOverrides={{ layoutType: AppLayoutType.Empty }}
        />
      </RoutesHOC>
      <RoutesHOC anonymous={true} path="/admin/login">
        <RouterOutlet
          component={LoginPage}
          path="/"
          nextUrl="/admin/"
          layoutOverrides={{ layoutType: AppLayoutType.Empty }}
        />
      </RoutesHOC>
      <RoutesHOC anonymous={true} path="/password-reset">
        <RouterOutlet
          component={PasswordResetRequestPage}
          path="/request"
          layoutOverrides={{
            layoutType: AppLayoutType.Empty,
            showHeader: true,
            headerText: "Forgot Password",
          }}
          default
        />
        <RouterOutlet
          component={PasswordResetRequestSuccessPage}
          path="/request/success"
          layoutOverrides={{ layoutType: AppLayoutType.Empty }}
        />
        <RouterOutlet
          component={PasswordResetConfirmPage}
          path="/confirm"
          layoutOverrides={{ layoutType: AppLayoutType.Empty }}
        />
        <RouterOutlet
          component={PasswordResetConfirmSuccessPage}
          path="/confirm/success"
          layoutOverrides={{ layoutType: AppLayoutType.Empty }}
        />
      </RoutesHOC>
      <RoutesHOC anonymous={true} path="/contact-us">
        <RouterOutlet
          component={ContactUsCard}
          path="/"
          layoutOverrides={{
            hideBackButton: true,
            headerText: "Contact Us",
            showHeader: true,
          }}
        />
      </RoutesHOC>
      <RoutesHOC hijacking={true} path="/user-hijacking">
        <RouterOutlet component={UserHijackingHijackPage} path="/hijack/:userId" />
        <RouterOutlet component={UserHijackingReleasePage} path="/release" />
      </RoutesHOC>
      <RoutesHOC anonymous={true} path="/user-sso">
        <RouterOutlet component={SsoAuthenticatorLogin} path="/login" />
        <RouterOutlet
          component={SsoSamlAuthenticatorSuccessRedirect}
          path="/saml/success"
        />
      </RoutesHOC>
    </Router>
  );
};

export default Main;
